import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Sparkles, Download, Copy, RefreshCw, Image as ImageIcon, BookOpen } from "lucide-react";

export const InstagramPostTab = () => {
  const [topic, setTopic] = useState("");
  const [description, setDescription] = useState("");
  const [themeType, setThemeType] = useState<"umbanda" | "geral">("umbanda");
  const [generating, setGenerating] = useState(false);
  const [regeneratingImage, setRegeneratingImage] = useState(false);
  const [regeneratingText, setRegeneratingText] = useState(false);
  const [generatedPostUrl, setGeneratedPostUrl] = useState<string | null>(null);
  const [generatedStoriesUrl, setGeneratedStoriesUrl] = useState<string | null>(null);
  const [generatedText, setGeneratedText] = useState<string>("");
  const [activeImageTab, setActiveImageTab] = useState<"post" | "stories">("post");
  const { toast } = useToast();
  
  const resultSectionRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to result when both image and text are generated
  useEffect(() => {
    if (generatedPostUrl && generatedStoriesUrl && generatedText && resultSectionRef.current) {
      resultSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [generatedPostUrl, generatedStoriesUrl, generatedText]);

  const handleGenerate = async () => {
    if (!topic.trim()) {
      toast({
        title: "Campo obrigatório",
        description: "Por favor, informe o tema do post",
        variant: "destructive",
      });
      return;
    }

    setGenerating(true);
    setGeneratedPostUrl(null);
    setGeneratedStoriesUrl(null);
    setGeneratedText("");

    try {
      const { data, error } = await supabase.functions.invoke('generate-instagram-post', {
        body: {
          topic: topic.trim(),
          description: description.trim() || undefined,
          themeType,
        },
      });

      if (error) {
        throw new Error(error.message || "Falha ao gerar post");
      }
      setGeneratedPostUrl(data.postImageUrl);
      setGeneratedStoriesUrl(data.storiesImageUrl);
      setGeneratedText(data.instagramText);

      toast({
        title: "Post gerado com sucesso!",
        description: "Imagens (Post + Stories) e texto prontos para o Instagram",
      });
    } catch (error: any) {
      console.error("Error generating post:", error);
      toast({
        title: "Erro ao gerar post",
        description: error.message || "Tente novamente mais tarde",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerateImage = async () => {
    if (!topic.trim()) return;

    setRegeneratingImage(true);
    setGeneratedPostUrl(null);
    setGeneratedStoriesUrl(null);

    try {
      const { data, error } = await supabase.functions.invoke('generate-instagram-post', {
        body: {
          topic: topic.trim(),
          description: description.trim() || undefined,
          themeType,
          regenerateOnly: "image",
        },
      });

      if (error) {
        throw new Error(error.message || "Falha ao regenerar imagens");
      }
      setGeneratedPostUrl(data.postImageUrl);
      setGeneratedStoriesUrl(data.storiesImageUrl);

      toast({
        title: "Imagens regeneradas!",
        description: "Post e Stories gerados com sucesso",
      });
    } catch (error: any) {
      console.error("Error regenerating images:", error);
      toast({
        title: "Erro ao regenerar imagens",
        description: error.message || "Tente novamente mais tarde",
        variant: "destructive",
      });
    } finally {
      setRegeneratingImage(false);
    }
  };

  const handleRegenerateText = async () => {
    if (!topic.trim()) return;

    setRegeneratingText(true);
    setGeneratedText("");

    try {
      const { data, error } = await supabase.functions.invoke('generate-instagram-post', {
        body: {
          topic: topic.trim(),
          description: description.trim() || undefined,
          themeType,
          regenerateOnly: "text",
        },
      });

      if (error) {
        throw new Error(error.message || "Falha ao regenerar texto");
      }
      setGeneratedText(data.instagramText);

      toast({
        title: "Texto regenerado!",
        description: "Novo texto gerado com sucesso",
      });
    } catch (error: any) {
      console.error("Error regenerating text:", error);
      toast({
        title: "Erro ao regenerar texto",
        description: error.message || "Tente novamente mais tarde",
        variant: "destructive",
      });
    } finally {
      setRegeneratingText(false);
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleDownload = async () => {
    if (!generatedPostUrl || !generatedStoriesUrl) return;

    const timestamp = Date.now();

    try {
      toast({
        title: "Iniciando downloads",
        description: "Baixando Post e Stories...",
      });

      // Download Post
      const postResponse = await fetch(generatedPostUrl);
      const postBlob = await postResponse.blob();
      downloadBlob(postBlob, `instagram-post-${timestamp}.png`);

      // Small delay between downloads
      await new Promise(resolve => setTimeout(resolve, 500));

      // Download Stories
      const storiesResponse = await fetch(generatedStoriesUrl);
      const storiesBlob = await storiesResponse.blob();
      downloadBlob(storiesBlob, `instagram-stories-${timestamp}.png`);

      toast({
        title: "Downloads concluídos",
        description: "Post e Stories baixados com sucesso",
      });
    } catch (error) {
      toast({
        title: "Erro no download",
        description: "Não foi possível baixar as imagens",
        variant: "destructive",
      });
    }
  };

  const handleCopyText = async () => {
    if (!generatedText) return;

    try {
      await navigator.clipboard.writeText(generatedText);
      toast({
        title: "Texto copiado!",
        description: "Cole no Instagram para publicar",
      });
    } catch (error) {
      toast({
        title: "Erro ao copiar",
        description: "Não foi possível copiar o texto",
        variant: "destructive",
      });
    }
  };

  const handleReset = () => {
    setTopic("");
    setDescription("");
    setThemeType("umbanda");
    setGeneratedPostUrl(null);
    setGeneratedStoriesUrl(null);
    setGeneratedText("");
    setActiveImageTab("post");
  };

  // Show result section only when both images AND text are generated (or during regeneration)
  const showResultSection = (generatedPostUrl && generatedStoriesUrl && generatedText) || regeneratingImage || regeneratingText;

  return (
    <div className="space-y-6">
      {/* Tips Card - FIRST */}
      <Card className="bg-muted/50">
        <CardHeader>
          <CardTitle className="text-lg">💡 Dicas para melhores resultados</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>• <strong>🕯️ Tema Umbanda:</strong> Use para Orixás, linhas espirituais, ervas, banhos, rituais - imagens 100% umbandistas</p>
          <p>• <strong>📢 Tema Geral:</strong> Use para divulgação de playlist, eventos, comunicados - imagens modernas com toque sutil de espiritualidade</p>
          <p>• <strong>Seja específico:</strong> "Banho de Arruda para proteção" gera melhor resultado que apenas "Banho"</p>
          <p>• <strong>Use a descrição:</strong> Explique o foco do post para conteúdo mais personalizado</p>
          <p>• <strong>Formatos:</strong> São geradas 2 imagens: Post (1080x1350) e Stories (1080x1920)</p>
        </CardContent>
      </Card>

      {/* Generator Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-display text-primary flex items-center gap-2">
            <Sparkles className="w-6 h-6" />
            Gerador de Posts Instagram
          </CardTitle>
          <CardDescription>
            Crie posts educativos sobre Umbanda com IA. A IA gera 2 imagens (Post + Stories) e um texto com hashtags.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Input Form */}
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="themeType">Tipo de Tema *</Label>
              <Select value={themeType} onValueChange={(value) => setThemeType(value as "umbanda" | "geral")} disabled={generating}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo de tema" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="umbanda">🕯️ Tema Umbanda (Orixás, linhas, rituais)</SelectItem>
                  <SelectItem value="geral">📢 Tema Geral/Divulgação (Eventos, comunicados, playlist)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {themeType === "umbanda" 
                  ? "Imagens 100% umbandistas com Orixás, entidades e elementos sagrados"
                  : "Imagens modernas e profissionais com toque sutil de espiritualidade"}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="topic">Tema do Post *</Label>
              <Input
                id="topic"
                placeholder={themeType === "umbanda" 
                  ? "Ex: Preto Velho, Oxum, Banho de Arruda, Caboclo..." 
                  : "Ex: Ouça nossos Pontos, Calendário de Giras, Evento Especial..."}
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                disabled={generating}
              />
              <p className="text-sm text-muted-foreground">
                {themeType === "umbanda"
                  ? "Nome do orixá, linha espiritual, erva, banho ou qualquer tema da Umbanda"
                  : "Título do evento, comunicado ou divulgação"}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição do Post (opcional)</Label>
              <Textarea
                id="description"
                placeholder={themeType === "umbanda"
                  ? "Descreva o que você quer comunicar neste post. Ex: Falar sobre os benefícios do banho de arruda..."
                  : "Descreva o conteúdo. Ex: Divulgar nossa playlist de pontos no Spotify com músicas gravadas no terreiro..."}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={generating}
                rows={4}
              />
              <p className="text-sm text-muted-foreground">
                Informações adicionais que a IA usará para personalizar o conteúdo
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleGenerate}
                disabled={generating || !topic.trim()}
                className="flex-1"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Gerando com IA...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Gerar Post com IA
                  </>
                )}
              </Button>
              
              {(generatedPostUrl || generatedStoriesUrl || generatedText) && (
                <Button variant="outline" onClick={handleReset} disabled={generating}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Limpar
                </Button>
              )}
            </div>
          </div>

          {/* Generated Content - Only shown when both images AND text exist */}
          {showResultSection && (
            <div ref={resultSectionRef} className="border-t pt-6">
              <h3 className="text-lg font-semibold mb-4">Resultado</h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Image Preview with Tabs */}
                <div className="space-y-3">
                  <Label>Imagens Geradas</Label>
                  
                  {regeneratingImage ? (
                    <div className="aspect-[1080/1350] bg-muted rounded-lg overflow-hidden border flex flex-col items-center justify-center gap-3">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                      <p className="text-sm text-muted-foreground font-medium">Imagens sendo geradas, aguarde...</p>
                    </div>
                  ) : (
                    <Tabs value={activeImageTab} onValueChange={(v) => setActiveImageTab(v as "post" | "stories")} className="w-full">
                      <TabsList className="w-full grid grid-cols-2">
                        <TabsTrigger value="post" className="flex items-center gap-2">
                          <ImageIcon className="w-4 h-4" />
                          Post (1080x1350)
                        </TabsTrigger>
                        <TabsTrigger value="stories" className="flex items-center gap-2">
                          <BookOpen className="w-4 h-4" />
                          Stories (1080x1920)
                        </TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="post" className="mt-3">
                        <div className="relative aspect-[1080/1350] bg-muted rounded-lg overflow-hidden border">
                          {generatedPostUrl ? (
                            <img
                              src={generatedPostUrl}
                              alt="Post gerado"
                              className="w-full h-full object-cover"
                            />
                          ) : null}
                        </div>
                      </TabsContent>
                      
                      <TabsContent value="stories" className="mt-3">
                        <div className="relative aspect-[1080/1920] bg-muted rounded-lg overflow-hidden border max-h-[500px]">
                          {generatedStoriesUrl ? (
                            <img
                              src={generatedStoriesUrl}
                              alt="Stories gerado"
                              className="w-full h-full object-contain"
                            />
                          ) : null}
                        </div>
                      </TabsContent>
                    </Tabs>
                  )}
                  
                  {generatedPostUrl && generatedStoriesUrl && !regeneratingImage && (
                    <div className="flex gap-2">
                      <Button 
                        onClick={handleRegenerateImage} 
                        variant="outline" 
                        className="flex-1"
                        disabled={regeneratingImage}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Regenerar Imagens
                      </Button>
                      <Button onClick={handleDownload} variant="secondary" className="flex-1">
                        <Download className="w-4 h-4 mr-2" />
                        Baixar Ambas
                      </Button>
                    </div>
                  )}
                </div>

                {/* Text Preview */}
                <div className="space-y-3">
                  <Label>Texto para Instagram</Label>
                  <div className="relative">
                    {regeneratingText ? (
                      <div className="flex flex-col items-center justify-center bg-muted rounded-md gap-3 h-[400px]">
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground font-medium">Texto sendo gerado, aguarde...</p>
                      </div>
                    ) : (
                      <Textarea
                        value={generatedText}
                        readOnly
                        rows={16}
                        className="resize-none font-sans text-sm"
                        placeholder=""
                      />
                    )}
                  </div>
                  {generatedText && !regeneratingText && (
                    <>
                      <div className="flex gap-2">
                        <Button 
                          onClick={handleRegenerateText} 
                          variant="outline" 
                          className="flex-1"
                          disabled={regeneratingText}
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Regenerar Texto
                        </Button>
                        <Button onClick={handleCopyText} variant="secondary" className="flex-1">
                          <Copy className="w-4 h-4 mr-2" />
                          Copiar
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground text-center">
                        {generatedText.length} / 2200 caracteres
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
